<template>
  <paper-modal-container :is-visible="isVisible" :title="modalTitle" :hide-close-button="false" @close="cancelPopup">
    <div :class="$style.formTypography">
    <br />

    <!-- 내 정보 가져오기 -->
    <div :class="$style.importBanner">
      <button type="button" :class="$style.importButton" @click="importFromMyProfile" :disabled="uploading">
        <img src="@/assets/images/account_circle.png" alt="내 정보" :class="$style.importIcon" />
        내 정보 가져오기
      </button>
      <p :class="$style.importDesc">프로필 이미지·닉네임·X 링크를 자동으로 채워드려요</p>
    </div>

    <div :class="$style.input">
      <h4>프로필 이미지 <span style="font-size: 12px"></span></h4>
      <profile-image-uploader :artist-name="artistName || '새 아티스트'" :profile-image="profileImageUrl"
        :artist-type="artistType" :show-controls="true" :mode="mode === 'update' ? 'update-artist' : 'new-artist'"
        @image-uploaded="handleImageUploaded" @image-deleted="handleImageDeleted" />
    </div>
    <div :class="$style.input">
      <h4 :class="$style.required">아티스트명</h4>
      <input :class="$style.textInput" v-model="artistName" placeholder="아티스트명을 입력하세요" :disabled="uploading" />
      <small v-if="mode === 'update'" style="color: #666; font-size: 12px">
        아티스트명을 변경하면 연결된 모든 공연에서 아티스트명이 변경됩니다.
      </small>
    </div>

    <div :class="$style.input">
      <h4 :class="$style.required">아티스트 분류</h4>
      <div :class="$style.radio">
        <label>
          <input type="radio" value="band" v-model="artistCategory" :disabled="uploading" />
          밴드
        </label>
        <label>
          <input type="radio" value="individual" v-model="artistCategory" :disabled="uploading" />
          개인
        </label>
      </div>
    </div>

    <!-- 개인일 때만 세부 포지션 선택 -->
    <div v-if="artistCategory === 'individual'" :class="$style.input">
      <h4 :class="$style.required">포지션</h4>

      <!-- 포지션 선택 체크박스 -->
      <div :class="$style.checkbox">
        <label v-for="position in availablePositions" :key="position.value">
          <input type="checkbox" :value="position.value" :checked="artistPositions.includes(position.value)" @change="
            handlePositionChange(position.value, $event.target.checked)
            " :disabled="uploading" />
          {{ position.label }}
        </label>
      </div>

      <!-- 선택된 포지션 우선순위 표시 -->
      <div v-if="artistPositions.length > 0" :class="$style.selectedPositions">
        <h5>선택된 포지션 (우선순위 순)</h5>
        <div :class="$style.positionList">
          <div v-for="(position, index) in artistPositions" :key="position" :class="$style.positionItem">
            <span :class="$style.positionRank">{{ index + 1 }}</span>
            <span :class="$style.positionName">{{
              getPositionLabel(position)
              }}</span>
            <div :class="$style.positionControls">
              <button type="button" :class="$style.moveButton" @click="movePosition(index, -1)"
                :disabled="index === 0 || uploading" title="위로 이동">
                ↑
              </button>
              <button type="button" :class="$style.moveButton" @click="movePosition(index, 1)"
                :disabled="index === artistPositions.length - 1 || uploading" title="아래로 이동">
                ↓
              </button>
              <button type="button" :class="$style.removeButton" @click="removePosition(position)" :disabled="uploading"
                title="제거">
                ✕
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 밴드 멤버 등록 (밴드 타입일 때만 표시) -->
    <div v-if="artistCategory === 'band'" :class="$style.input">
      <h4>
        밴드 멤버 <span style="font-size: 12px">(검색 또는 직접 입력)</span>
      </h4>
      <div v-if="selectedMembers.length > 0" :class="$style.artistList">
        <div v-for="(member, index) in selectedMembers" :key="`member-${member.id || member.name}-${index}`"
          :class="$style.artistItem">
          <div :class="$style.artistInfo">
            <div :class="$style.selectedProfileWrap">
              <img :src="getArtistOptionImage(member)" :alt="member.name" :class="$style.selectedProfileImage" />
            </div>
            <div :class="$style.artistDetails">
              <div :class="$style.artistName">{{ member.name }}</div>
              <div :class="$style.artistType">{{ getMemberType(member.id) }}</div>
            </div>
          </div>
          <button type="button" @click="removeMember(index)" :class="$style.memberRemoveButton" :disabled="uploading">
            삭제
          </button>
        </div>
      </div>
      <div :class="$style.addArtistForm">
        <AutoComplete ref="memberAutocompleteRef" v-model="currentMemberInput" :suggestions="filteredMembers"
          @complete="searchMembers" @item-select="addMemberFromSuggestion" @keyup.enter.prevent="addMemberManually"
          @input="handleMemberInput" @blur="handleMemberBlur" @compositionstart="handleCompositionStart"
          @compositionend="handleCompositionEnd" placeholder="멤버 검색 또는 입력" :disabled="uploading"
          :class="[$style.autocomplete, $style.memberAutocomplete]" dropdown :forceSelection="false" optionLabel="label"
          panelClass="member-autocomplete-panel">
          <template #option="{ option }">
            <div :class="$style.artistOption">
              <img :src="getArtistOptionImage(option)" :alt="option.name" :class="$style.optionProfileImage" />
              <div :class="$style.optionDetails">
                <span :class="$style.optionName">{{ option.name }}</span>
                <span :class="$style.optionType">{{ getArtistTypeLabel(option.type) }}</span>
              </div>
            </div>
          </template>
        </AutoComplete>
        <button type="button" :class="$style.addButton" @click="addMemberManually"
          :disabled="uploading || !getNormalizedInputText(currentMemberInput).trim()">
          추가
        </button>
      </div>
    </div>

    <!-- 소속 밴드 등록 (개인 타입일 때만 표시) -->
    <div v-if="artistCategory === 'individual'" :class="$style.input">
      <h4>
        소속 밴드 <span style="font-size: 12px">(검색 또는 직접 입력)</span>
      </h4>
      <div v-if="selectedBands.length > 0" :class="$style.artistList">
        <div v-for="(band, index) in selectedBands" :key="`band-${band.id || band.name}-${index}`"
          :class="$style.artistItem">
          <div :class="$style.artistInfo">
            <div :class="$style.selectedProfileWrap">
              <img :src="getArtistOptionImage(band)" :alt="band.name" :class="$style.selectedProfileImage" />
            </div>
            <div :class="$style.artistDetails">
              <div :class="$style.artistName">{{ band.name }}</div>
              <div :class="$style.artistType">{{ getMemberType(band.id) }}</div>
            </div>
          </div>
          <button type="button" @click="removeBand(index)" :class="$style.memberRemoveButton" :disabled="uploading">
            삭제
          </button>
        </div>
      </div>
      <div :class="$style.addArtistForm">
        <AutoComplete ref="bandAutocompleteRef" v-model="currentBandInput" :suggestions="filteredBands"
          @complete="searchBands" @item-select="addBandFromSuggestion" @keyup.enter.prevent="addBandManually"
          @input="handleBandInput" @blur="handleBandBlur" @compositionstart="handleCompositionStart"
          @compositionend="handleCompositionEnd" placeholder="밴드 검색 또는 입력" :disabled="uploading"
          :class="[$style.autocomplete, $style.memberAutocomplete]" dropdown :forceSelection="false" optionLabel="label"
          panelClass="band-autocomplete-panel">
          <template #option="{ option }">
            <div :class="$style.artistOption">
              <img :src="getArtistOptionImage(option)" :alt="option.name" :class="$style.optionProfileImage" />
              <div :class="$style.optionDetails">
                <span :class="$style.optionName">{{ option.name }}</span>
                <span :class="$style.optionType">{{ getArtistTypeLabel(option.type) }}</span>
              </div>
            </div>
          </template>
        </AutoComplete>
        <button type="button" :class="$style.addButton" @click="addBandManually"
          :disabled="uploading || !getNormalizedInputText(currentBandInput).trim()">
          추가
        </button>
      </div>
    </div>

    <div :class="$style.input">
      <h4>상세정보 링크 <span style="font-size: 12px"></span></h4>
      <input :class="$style.textInput" v-model="detailLink" placeholder="트위터, 인스타그램 등 SNS 링크를 입력하세요"
        :disabled="uploading" />
    </div>

    <div :class="$style.input">
      <h4>소개 <span style="font-size: 12px"></span></h4>
      <textarea :class="$style.textArea" v-model="description" placeholder="아티스트 소개를 입력하세요" :disabled="uploading"
        rows="4" />
    </div>

    <!-- SNS 링크 섹션 -->
    <div :class="$style.snsSection">
      <h4 :class="$style.snsSectionTitle">
        SNS 링크 <span style="font-size: 12px"></span>
      </h4>

      <!-- SNS 리스트 -->
      <div v-for="(sns, index) in snsList" :key="index" :class="$style.snsRow">
        <span :class="$style.snsTypeLabel">{{ getSnsLabel(sns.type) }}</span>
        <input v-model="sns.url" :class="$style.snsInput" type="url" :placeholder="getSnsPlaceholder(sns.type)"
          :disabled="uploading" />
        <button type="button" :class="$style.snsRemoveBtn" @click="removeSns(index)" :disabled="uploading">
          ✕
        </button>
      </div>

      <!-- SNS 타입 선택 커스텀 드롭다운 -->
      <div :class="$style.snsDropdownWrapper">
        <button type="button" :class="$style.snsDropdownToggle" @click="toggleSnsDropdown" :disabled="uploading">
          + SNS 추가
          <span :class="[$style.dropdownArrow, { [$style.dropdownArrowOpen]: snsDropdownOpen }]">▼</span>
        </button>
        <div v-if="snsDropdownOpen" :class="$style.snsDropdownMenu">
          <button v-for="opt in availableSnsOptions" :key="opt.value" type="button" :class="$style.snsDropdownItem"
            @click="selectSnsType(opt.value)">
            {{ opt.label }}
          </button>
        </div>
      </div>
    </div>

    <!-- 관리자 전용: userEmail 수정 -->
    <div v-if="userStore.auth === 'admin'" :class="$style.input">
      <h4>사용자 이메일 <span style="font-size: 12px">(관리자 전용)</span></h4>
      <input :class="$style.textInput" v-model="userEmail" placeholder="아티스트 소유자 이메일을 입력하세요" :disabled="uploading" />
      <small style="color: #666; font-size: 12px; margin-top: 5px; display: block">
        이 필드는 관리자만 수정할 수 있습니다. 수정 권한 부여에 사용됩니다.
      </small>
    </div>

    <div :class="$style.buttons">
      <button :class="$style.cancel" @click="cancelPopup" :disabled="uploading">
        취소
      </button>
      <button :class="$style.register" @click="submitForm" :disabled="!isFormValid || uploading">
        <span v-if="!uploading">{{
          mode === "update" ? "수정" : "등록"
        }}</span>
        <span v-else> <i :class="$style.spinner"></i> </span>
      </button>
    </div>
    </div>
  </paper-modal-container>
</template>

<script>
import {
  collection,
  getDocs,
} from "firebase/firestore";
import { ref as storageRef, ref as storageRefUtil } from "firebase/storage";
import { getDownloadURL } from "@/utils/storageWithNavigation";
import { firestore, storage } from "/firebaseConfig";
import { useUserStore } from "@/store/user";
import { createArtist as createArtistApi, updateArtist as updateArtistApi, renameArtist } from "@/utils/write/artist";
import { copyStorageFile } from "@/utils/write/storage";
import PaperModalContainer from "@/components/modal/PaperModalContainer.vue";
import ProfileImageUploader from "@/components/ProfileImageUploader.vue";
import AutoComplete from "primevue/autocomplete";
import { ARTIST_POSITION_OPTIONS, getArtistTypeDisplayName } from "@/utils/artistTypes";
import { getUserInfo } from "@/utils/read/user";

export default {
  name: "ArtistForm",
  components: {
    PaperModalContainer,
    ProfileImageUploader,
    AutoComplete,
  },
  props: {
    isVisible: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: "create", // 'create' or 'update'
    },
    artistData: {
      type: Object,
      default: null,
    },
  },
  emits: ["close-popup", "artist-updated"],
  data() {
    return {
      artistId: null, // 아티스트 문서 ID (수정 모드에서 사용)
      artistName: "",
      originalArtistName: "", // 원본 아티스트명 (수정 모드에서 이름 변경 감지용)
      artistCategory: "", // "band" 또는 "individual"
      artistPositions: [], // 개인일 때의 세부 포지션들
      detailLink: "",
      description: "",
      userEmail: "", // 아티스트 소유자 이메일 (관리자 전용)
      snsList: [], // SNS 링크 배열 [{ type: 'youtube', url: '' }, ...]
      snsDropdownOpen: false, // SNS 드롭다운 열림 상태
      snsTypeOptions: [
        { value: "youtube", label: "유튜브" },
        { value: "instagram", label: "인스타그램" },
        { value: "twitter", label: "X (트위터)" },
        { value: "tiktok", label: "틱톡" },
        { value: "soundcloud", label: "사운드클라우드" },
      ],
      profileImageUrl: null,
      uploadedImageFileName: null, // 새로 업로드한 이미지 파일명 (취소 시 삭제용)
      originalImageFileName: null, // 원본 이미지 파일명 (수정 모드에서 사용)
      originalImageUrl: null, // 원본 이미지 URL (수정 모드 취소 시 복구용)
      isImageDeleted: false, // 이미지 삭제 플래그 (수정 모드에서 사용)
      uploading: false,
      userStore: useUserStore(),
      // 포지션 옵션
      availablePositions: ARTIST_POSITION_OPTIONS,
      // 밴드 멤버 관련
      selectedMembers: [],
      currentMemberInput: "",
      allArtists: [],
      artistsWithTypes: [], // 아티스트 이름과 타입 정보
      filteredMembers: [],
      // 소속 밴드 관련 (개인 아티스트용)
      selectedBands: [],
      currentBandInput: "",
      filteredBands: [],
      artistImageLoadPromises: {},
      isComposing: false, // 한글 조합 중인지 확인
      justSelectedFromSuggestion: false, // 방금 드롭다운에서 선택했는지 여부
      dropdownObserver: null, // 드롭다운 observer
    };
  },
  computed: {
    modalTitle() {
      return this.mode === "update"
        ? "아티스트 정보 수정"
        : "아티스트 등록 신청";
    },
    isFormValid() {
      const hasName = this.artistName.trim() !== "";
      const hasCategory = this.artistCategory !== "";
      const hasPositions =
        this.artistCategory === "band" ||
        (this.artistCategory === "individual" &&
          this.artistPositions.length > 0);
      return hasName && hasCategory && hasPositions;
    },
    // SNS 타입 옵션 (동일 타입도 추가 가능)
    availableSnsOptions() {
      return this.snsTypeOptions;
    },
    // ProfileImageUploader에 전달할 아티스트 타입
    artistType() {
      if (this.artistCategory === "band") {
        return "band";
      } else if (this.artistCategory === "individual" && this.artistPositions.length > 0) {
        this.artistPositions;
      }
      return null;
    },
  },
  watch: {
    async isVisible(newVal) {
      if (newVal) {
        await this.initializeForm();
      }
    },
    artistData: {
      async handler() {
        if (this.artistData && this.mode === "update") {
          // artistsWithTypes가 로드되지 않았으면 먼저 로드
          if (this.artistsWithTypes.length === 0) {
            await this.loadArtists();
          }
          await this.loadArtistData();
        }
      },
      immediate: true,
    },
  },
  async mounted() {
    await this.loadArtists();

    // 드롭다운 패널이 나타날 때 너비 조정
    this.$nextTick(() => {
      const observer = new MutationObserver(() => {
        const memberPanel = document.querySelector(
          ".member-autocomplete-panel"
        );
        const bandPanel = document.querySelector(
          ".band-autocomplete-panel"
        );

        if (memberPanel && memberPanel.style.display !== "none") {
          this.adjustDropdownWidth();
        }
        if (bandPanel && bandPanel.style.display !== "none") {
          this.adjustDropdownWidth();
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["style", "class"],
      });

      // 컴포넌트 해제 시 observer 정리를 위해 observer를 data에 저장
      this.dropdownObserver = observer;
    });
  },
  beforeUnmount() {
    // observer 정리
    if (this.dropdownObserver) {
      this.dropdownObserver.disconnect();
    }
  },
  methods: {
    async initializeForm() {
      if (this.mode === "create") {
        // 이미 입력된 데이터가 있으면 초기화하지 않음
        const hasExistingData =
          this.artistName ||
          this.artistCategory ||
          this.detailLink ||
          this.profileImageUrl;
        if (!hasExistingData) {
          this.resetForm();
        }
      } else if (this.mode === "update" && this.artistData) {
        await this.loadArtistData();
        // 수정 모드 초기화 시 삭제 플래그도 초기화
        this.isImageDeleted = false;
      }
    },

    resetForm() {
      this.artistId = null;
      this.artistName = "";
      this.originalArtistName = "";
      this.artistCategory = "";
      this.artistPositions = [];
      this.detailLink = "";
      this.description = "";
      this.userEmail = "";
      this.snsList = [];
      this.snsDropdownOpen = false;
      this.profileImageUrl = null;
      this.uploadedImageFileName = null;
      this.originalImageFileName = null;
      this.originalImageUrl = null;
      this.isImageDeleted = false;
      this.selectedMembers = [];
      this.currentMemberInput = "";
      this.selectedBands = [];
      this.currentBandInput = "";
      this.isComposing = false;
    },

    async loadArtistData() {
      if (this.artistData) {

        this.artistId = this.artistData.id || null; // 문서 ID 저장
        this.artistName = this.artistData.name || "";
        this.originalArtistName = this.artistData.name || ""; // 원본 이름 저장

        // category 필드가 있으면 새로운 구조
        if (this.artistData.category) {
          this.artistCategory = this.artistData.category;
          if (
            this.artistCategory === "individual" &&
            Array.isArray(this.artistData.type)
          ) {
            // 'mix'를 'other'로 변환 (동일한 값으로 처리)
            this.artistPositions = this.artistData.type.map((t) =>
              t === "mix" ? "other" : t
            );
          } else {
            this.artistPositions = [];
          }
        } else {
          // 기존 데이터 호환성
          let types = [];
          if (Array.isArray(this.artistData.type)) {
            types = [...this.artistData.type];
          } else if (this.artistData.type) {
            types = [this.artistData.type];
          }

          // 'mix'를 'other'로 변환 (동일한 값으로 처리)
          types = types.map((t) => (t === "mix" ? "other" : t));

          if (types.includes("band")) {
            this.artistCategory = "band";
            this.artistPositions = [];
          } else {
            this.artistCategory = "individual";
            this.artistPositions = types.filter((type) => type !== "band");
          }
        }

        this.detailLink = this.artistData.detailLink || "";
        this.description = this.artistData.description || "";
        this.userEmail = this.artistData.userEmail || "";
        // SNS 리스트 로드 (기존 youtubeChannel, instagram 필드 호환 + 새로운 snsList 형식)
        this.snsList = [];
        if (this.artistData.snsList && Array.isArray(this.artistData.snsList)) {
          this.snsList = this.artistData.snsList.map((s) => ({ ...s }));
        } else {
          // 기존 필드 호환
          if (this.artistData.youtubeChannel) {
            this.snsList.push({ type: "youtube", url: this.artistData.youtubeChannel });
          }
          if (this.artistData.instagram) {
            this.snsList.push({ type: "instagram", url: this.artistData.instagram });
          }
        }
        // 수정 모드에서는 기존 이미지 정보를 설정
        // profileImage(URL)를 우선하되, 없으면 img 필드(파일명) 사용
        const originalImageUrl = this.artistData.profileImage || this.artistData.img || null;
        this.profileImageUrl = originalImageUrl;
        // 원본 이미지 파일명 저장 (수정 모드에서 사용)
        this.originalImageFileName = this.artistData.img || null;
        // 원본 이미지 URL 저장 (취소 시 복구용)
        this.originalImageUrl = originalImageUrl;
        // 삭제 플래그 초기화
        this.isImageDeleted = false;
        // 밴드 멤버 정보 로드 (ID를 이름으로 변환)
        if (this.artistData.members && Array.isArray(this.artistData.members)) {
          const memberArtists = this.artistData.members
            .map(memberId => this.artistsWithTypes.find(a => a.id === memberId))
            .filter(Boolean);
          // artistsWithTypes 원본 객체에 이미지 URL을 먼저 로드한 뒤 복사
          await this.loadProfileImagesForArtists(memberArtists);
          this.selectedMembers = memberArtists.map(artist => ({
            id: artist.id,
            name: artist.name,
            type: artist.type || null,
            profileImage: artist.profileImage || null,
          }));
        } else {
          this.selectedMembers = [];
        }
        // 소속 밴드 정보 로드 (ID를 이름으로 변환)
        // myBands는 소문자 m으로 시작할 수도 있음
        const myBandsData = this.artistData.myBands || this.artistData.MyBands || [];

        if (Array.isArray(myBandsData) && myBandsData.length > 0) {
          const bandArtists = myBandsData
            .map(bandId => this.artistsWithTypes.find(a => a.id === bandId))
            .filter(Boolean);
          // artistsWithTypes 원본 객체에 이미지 URL을 먼저 로드한 뒤 복사
          await this.loadProfileImagesForArtists(bandArtists);
          this.selectedBands = bandArtists.map(band => ({
            id: band.id,
            name: band.name,
            type: band.type || null,
            profileImage: band.profileImage || null,
          }));
        } else {
          this.selectedBands = [];
        }
      }
    },

    handleImageUploaded(imageData) {
      // 이미지 데이터가 유효한지 확인
      if (!imageData) {
        console.error("유효하지 않은 이미지 데이터:", imageData);
        return;
      }

      let imageUrl = null;
      let fileName = null;

      // 객체 형태로 전달된 경우 (url, fileName 포함)
      if (typeof imageData === "object" && imageData.url) {
        imageUrl = imageData.url;
        fileName = imageData.fileName;
      }
      // 문자열 형태로 전달된 경우 (URL 또는 파일명)
      else if (typeof imageData === "string") {
        imageUrl = imageData;

        // URL에서 파일명 추출 시도
        if (imageData.startsWith("http")) {
          try {
            // Firebase Storage URL 형식: https://firebasestorage.googleapis.com/.../o/artist%2Ffilename?alt=media...
            const urlParts = imageData.split('/o/')[1];
            if (urlParts) {
              const encodedPath = urlParts.split('?')[0];
              const fullPath = decodeURIComponent(encodedPath);
              // 'artist/filename.jpg' 형식
              const pathParts = fullPath.split('/');
              if (pathParts.length >= 2) {
                fileName = pathParts[pathParts.length - 1]; // 마지막 부분만 (파일명)
              }
            }
          } catch (e) {
            console.error("URL에서 파일명 추출 실패:", e);
          }
        } else {
          // 이미 파일명인 경우
          fileName = imageData;
        }
      }

      // 업로드된 이미지 파일명 저장 (취소 시 삭제용)
      this.uploadedImageFileName = fileName;

      // 모든 모드에서 URL 설정
      this.profileImageUrl = imageUrl || imageData;

      // 화면에 바로 반영되도록 강제 업데이트
      this.$nextTick(() => {
        this.$forceUpdate();
      });
    },

    handleImageDeleted() {
      // 이미지 삭제 처리
      this.profileImageUrl = null;

      // 신규 등록 모드: 업로드한 파일은 이미 ProfileImageModal에서 삭제됨
      // uploadedImageFileName 초기화 (이미 삭제되었으므로)
      if (this.mode === "create") {
        this.uploadedImageFileName = null;
      }
      // 수정 모드: 삭제 플래그만 설정 (파일은 그대로 두고, 저장 시 처리)
      else if (this.mode === "update") {
        this.isImageDeleted = true;
        // 업로드한 파일이 있으면 취소 시 삭제 대상에서 제외
        // (이미 삭제된 것으로 처리되므로)
      }
    },

    // 포지션 관련 메서드들
    handlePositionChange(position, isChecked) {
      if (isChecked) {
        // 최대 4개 제한 체크
        if (this.artistPositions.length >= 4) {
          this.$alert("포지션은 최대 4개까지만 설정할 수 있습니다.");
          // 체크박스 상태를 원래대로 되돌림
          this.$nextTick(() => {
            const checkbox = document.querySelector(
              `input[value="${position}"]`
            );
            if (checkbox) {
              checkbox.checked = false;
            }
          });
          return;
        }
        // 체크된 경우 배열에 추가
        if (!this.artistPositions.includes(position)) {
          this.artistPositions.push(position);
        }
      } else {
        // 체크 해제된 경우 배열에서 제거
        this.removePosition(position);
      }
    },

    getPositionLabel(position) {
      const found = this.availablePositions.find((p) => p.value === position);
      return found ? found.label : position;
    },

    movePosition(index, direction) {
      const newIndex = index + direction;
      if (newIndex < 0 || newIndex >= this.artistPositions.length) {
        return;
      }

      // 배열에서 요소들의 위치를 바꿈
      const positions = [...this.artistPositions];
      [positions[index], positions[newIndex]] = [
        positions[newIndex],
        positions[index],
      ];
      this.artistPositions = positions;
    },

    removePosition(position) {
      const index = this.artistPositions.indexOf(position);
      if (index > -1) {
        this.artistPositions.splice(index, 1);
      }
    },

    async submitForm() {
      if (!this.isFormValid) {
        if (this.artistName.trim() === "") {
          this.$alert("아티스트명을 입력해주세요.");
          return;
        }
        if (this.artistCategory === "") {
          this.$alert("아티스트 분류를 선택해주세요.");
          return;
        }
        if (
          this.artistCategory === "individual" &&
          this.artistPositions.length === 0
        ) {
          this.$alert("포지션을 선택해주세요.");
          return;
        }
        return;
      }

      if (!this.userStore.isLogin) {
        this.$alert("로그인이 필요합니다.");
        return;
      }

      try {
        this.uploading = true;

        let succeeded = false;
        if (this.mode === "update") {
          succeeded = await this.updateArtist();
        } else {
          succeeded = await this.createArtist();
        }

        // API 실패(throw) · 조기 종료(false) 시 폼 유지, 성공(true)일 때만 닫기
        if (!succeeded) {
          return;
        }

        // 저장 성공 시 상태 초기화
        this.uploadedImageFileName = null;
        this.isImageDeleted = false;
        this.closePopup();
      } catch (error) {
        console.error("아티스트 등록 오류:", error);
        this.$alert("등록 중 오류가 발생했습니다. 다시 시도해주세요.");
        // 에러 발생 시에도 업로드된 이미지는 유지 (사용자가 다시 시도할 수 있도록)
      } finally {
        this.uploading = false;
      }
    },

    async checkDuplicateArtistName() {
      try {
        // artists 컬렉션에서 중복 체크
        const artistsSnapshot = await collection(firestore, "artists");
        const artistsQuery = await getDocs(artistsSnapshot);

        for (const docSnapshot of artistsQuery.docs) {
          if (docSnapshot.data().name === this.artistName.trim()) {
            return true;
          }
        }

        return false;
      } catch (error) {
        console.error("중복 체크 오류:", error);
        return false;
      }
    },

    async createArtist() {
      // 중복 아티스트명 체크
      if (await this.checkDuplicateArtistName()) {
        this.$alert("이미 등록된 아티스트명입니다.");
        return false;
      }

      // 이미지 파일명 추출
      let imageFileName = null;
      if (this.profileImageUrl) {
        // 업로드된 파일명이 있으면 우선 사용
        if (this.uploadedImageFileName) {
          imageFileName = this.uploadedImageFileName;
        } else if (this.profileImageUrl.startsWith("http")) {
          // URL에서 파일명 추출
          try {
            const urlParts = this.profileImageUrl.split('/o/')[1];
            if (urlParts) {
              const encodedPath = urlParts.split('?')[0];
              const fullPath = decodeURIComponent(encodedPath);
              const pathParts = fullPath.split('/');
              if (pathParts.length >= 2) {
                imageFileName = pathParts[pathParts.length - 1];
              }
            }
          } catch (e) {
            // URL 파싱 실패 시 다른 방법 시도
            const urlParts = this.profileImageUrl.split("/");
            const fileWithParams = urlParts[urlParts.length - 1];
            imageFileName = fileWithParams.split("?")[0];
            imageFileName = decodeURIComponent(imageFileName);
          }
        } else {
          // 이미 파일명인 경우
          imageFileName = this.profileImageUrl;
        }
      }

      // 타입 정보 생성
      let typeInfo;
      if (this.artistCategory === "band") {
        typeInfo = ["band"];
      } else {
        typeInfo = [...this.artistPositions];
      }

      const artistData = {
        type: typeInfo, // 배열로 저장
        category: this.artistCategory, // 새로운 필드: "band" 또는 "individual"
        detailLink: this.detailLink.trim() || null,
        description: this.description.trim() || null,
        img: imageFileName, // 파일명 저장
        members: this.artistCategory === "band" ? this.selectedMembers.map(m => m.id) : null, // ID만 저장
        myBands: this.artistCategory === "individual" ? this.selectedBands.map(b => b.id) : null, // 소속 밴드 ID 저장
        snsList: this.snsList.filter((s) => s.type && s.url.trim()).map((s) => ({ type: s.type, url: s.url.trim() })),
        regId: this.userStore.email,
        regNickname: this.userStore.nickname,
        approved: true, // 바로 승인
        createdBy: this.userStore.email,
        createdByNickname: this.userStore.nickname,
        registrationSource: "artist-form", // 등록 경로 구분
      };

      // artists 컬렉션에 저장 (아티스트명을 문서 ID로 사용)
      await createArtistApi(this.artistName.trim(), artistData);

      this.$alert("아티스트가 등록되었습니다.");
      return true;
    },

    async updateArtist() {
      // 이미지 파일명 추출
      let imageFileName = null;
      if (this.profileImageUrl) {
        // 업로드된 파일명이 있으면 우선 사용
        if (this.uploadedImageFileName) {
          imageFileName = this.uploadedImageFileName;
        } else if (this.profileImageUrl.startsWith("http")) {
          // URL에서 파일명 추출
          try {
            const urlParts = this.profileImageUrl.split('/o/')[1];
            if (urlParts) {
              const encodedPath = urlParts.split('?')[0];
              const fullPath = decodeURIComponent(encodedPath);
              const pathParts = fullPath.split('/');
              if (pathParts.length >= 2) {
                imageFileName = pathParts[pathParts.length - 1];
              }
            }
          } catch (e) {
            // URL 파싱 실패 시 다른 방법 시도
            const urlParts = this.profileImageUrl.split("/");
            const fileWithParams = urlParts[urlParts.length - 1];
            imageFileName = fileWithParams.split("?")[0];
            imageFileName = decodeURIComponent(imageFileName);
          }
        } else {
          // 이미 파일명인 경우
          imageFileName = this.profileImageUrl;
        }
      }

      // 이미지 삭제된 경우 처리
      if (this.isImageDeleted) {
        // 기존 파일이 있었던 경우 삭제
        if (this.originalImageFileName) {
          try {
            const { deleteImage } = await import("@/utils/write/storage");
            await deleteImage("artist", this.originalImageFileName);
          } catch (error) {
            console.error("기존 이미지 삭제 실패:", error);
            // 삭제 실패해도 계속 진행
          }
        }
        // 문서에서 공백 처리 (img 필드를 빈 문자열로)
        imageFileName = "";
      }

      // 기존 파일명과 새 파일명 비교 (이미지가 삭제되지 않은 경우)
      const isImageChanged = !this.isImageDeleted && imageFileName !== this.originalImageFileName;
      const oldImageFileName = this.originalImageFileName;

      // 타입 정보 생성
      let typeInfo;
      if (this.artistCategory === "band") {
        typeInfo = ["band"];
      } else {
        typeInfo = [...this.artistPositions];
      }

      const updateData = {
        type: typeInfo, // 배열로 저장
        category: this.artistCategory, // 새로운 필드: "band" 또는 "individual"
        detailLink: this.detailLink.trim() || null,
        description: this.description.trim() || null,
        members: this.artistCategory === "band" ? this.selectedMembers.map(m => m.id) : null, // ID만 저장
        myBands: this.artistCategory === "individual" ? this.selectedBands.map(b => b.id) : null, // 소속 밴드 ID 저장
        snsList: this.snsList.filter((s) => s.type && s.url.trim()).map((s) => ({ type: s.type, url: s.url.trim() })),
        lastModifiedBy: this.userStore.email,
        lastModifiedByNickname: this.userStore.nickname,
      };

      // 이미지 처리
      if (this.isImageDeleted) {
        // 삭제된 경우: img 필드를 빈 문자열로 설정 (백엔드에서 null 필터링 방지)
        updateData.img = "";
      } else if (isImageChanged && imageFileName) {
        // 변경된 경우: 새 파일명 설정
        updateData.img = imageFileName;
      }
      // 같으면 img 필드를 업데이트 데이터에서 제외 (문서 업데이트 X)

      // 관리자만 userEmail 수정 가능
      if (this.userStore.auth === "admin") {
        updateData.userEmail = this.userEmail.trim() || null;
      }

      // artists 컬렉션에서 해당 아티스트 업데이트 (ID로 문서 찾기)
      if (!this.artistId) {
        this.$alert("아티스트 ID를 찾을 수 없습니다.");
        return false;
      }

      // 아티스트명이 변경된 경우 먼저 관련 컬렉션 모두 업데이트
      const newArtistName = this.artistName.trim();
      if (this.originalArtistName && newArtistName && this.originalArtistName !== newArtistName) {
        try {
          await renameArtist(this.originalArtistName, newArtistName);
        } catch (renameError) {
          console.error("아티스트 이름 변경 오류:", renameError);
          throw renameError; // 이름 변경 실패 시 전체 실패 처리
        }
      }

      await updateArtistApi(this.artistId, updateData);

      // 이미지가 변경된 경우 기존 파일 삭제
      if (isImageChanged && oldImageFileName) {
        try {
          const { deleteImage } = await import("@/utils/write/storage");
          await deleteImage("artist", oldImageFileName);
        } catch (error) {
          console.error("기존 이미지 삭제 실패:", error);
          // 삭제 실패해도 계속 진행 (사용자에게 알리지 않음)
        }
      }

      this.$alert("아티스트 정보가 성공적으로 수정되었습니다.");

      // 부모 컴포넌트에 업데이트 완료 알림
      this.$emit("artist-updated", {
        id: this.artistId,
        name: this.artistName.trim(),
        originalName: this.originalArtistName,
      });
      return true;
    },

    async cancelPopup() {
      // 취소 시 업로드된 이미지 삭제 (await로 완료 대기)
      await this.cleanupUploadedImage();

      // 수정 모드에서 이미지가 삭제된 경우: 원래 상태로 복구
      if (this.mode === "update" && this.isImageDeleted) {
        // 삭제 플래그 초기화
        this.isImageDeleted = false;
        // 원본 이미지 URL로 복구 (파일/문서 변경 없이 현행 유지)
        if (this.originalImageUrl) {
          this.profileImageUrl = this.originalImageUrl;
        }
      }

      this.closePopup();
    },

    /**
     * 취소 시 업로드된 이미지 삭제
     * - 신규 등록 모드: 업로드한 이미지 삭제
     * - 수정 모드: 새로 업로드한 이미지가 있으면 삭제 (원본과 다른 경우)
     * - 삭제 실패해도 에러 처리하지 않음
     */
    async cleanupUploadedImage() {
      // 새로 업로드한 이미지가 있는 경우에만 삭제
      if (!this.uploadedImageFileName) {
        console.warn("삭제할 업로드 이미지가 없음");
        return;
      }

      const fileNameToDelete = this.uploadedImageFileName;

      // 신규 등록 모드: 업로드한 이미지 삭제
      if (this.mode === "create") {
        try {
          const { deleteImage } = await import("@/utils/write/storage");
          await deleteImage("artist", fileNameToDelete);
          // 삭제 성공 후 파일명 초기화
          this.uploadedImageFileName = null;
        } catch (error) {
          console.error("취소된 이미지 삭제 실패:", error);
          // 삭제 실패해도 계속 진행 (에러 처리 안 함)
        }
      }
      // 수정 모드: 새로 업로드한 이미지가 원본과 다른 경우 삭제
      else if (this.mode === "update") {
        // 이미지가 삭제되지 않고, 새로 업로드한 이미지가 원본과 다른 경우에만 삭제
        if (!this.isImageDeleted && this.uploadedImageFileName !== this.originalImageFileName) {
          try {
            const { deleteImage } = await import("@/utils/write/storage");
            await deleteImage("artist", fileNameToDelete);
            // 삭제 성공 후 파일명 초기화
            this.uploadedImageFileName = null;
          } catch (error) {
            console.error("취소된 이미지 삭제 실패:", error);
            // 삭제 실패해도 계속 진행 (에러 처리 안 함)
          }
        }
      }
    },

    closePopup() {
      this.resetForm();
      this.$emit("close-popup");
    },

    // 아티스트 목록 로드
    async loadArtists() {
      try {
        const artistsSnapshot = await getDocs(collection(firestore, "artists"));
        this.artistsWithTypes = artistsSnapshot.docs
          .map((doc) => {
            const data = doc.data();
            return {
              id: doc.id, // 문서 ID 추가
              name: data.name,
              type: data.type || null,
              profileImage:
                data.profileImage ||
                (typeof data.img === "string" && data.img.startsWith("http")
                  ? data.img
                  : null),
              imgFileName: typeof data.img === "string" && !data.img.startsWith("http") ? data.img : null,
              userProfileImg: data.userProfileImg || null,
              profileImageLoaded: !!data.profileImage || (typeof data.img === "string" && data.img.startsWith("http")),
              label: data.name,
              approved: data.approved || false,
            };
          })
          .filter((artist) => artist.approved && artist.name && artist.name.trim() !== "");

        this.allArtists = this.artistsWithTypes;

      } catch (error) {
        console.error("아티스트 목록 로드 오류:", error);
        this.allArtists = [];
        this.artistsWithTypes = [];
      }
    },

    // 멤버 관련 메서드들
    async searchMembers(event) {
      const query = (event.query || "").toLowerCase();
      const selectedMemberIds = this.selectedMembers.map(m => m.id);

      this.filteredMembers = this.allArtists.filter((artist) => {
        return (
          artist.name.toLowerCase().includes(query) &&
          !selectedMemberIds.includes(artist.id)
        );
      });
      await this.loadProfileImagesForArtists(this.filteredMembers.slice(0, 20));

      // 검색 결과가 나타날 때 드롭다운 너비 조정
      this.$nextTick(() => {
        this.adjustDropdownWidth();
      });
    },

    addMemberFromSuggestion(event) {
      const selected = event.value;
      const memberName = selected?.name?.trim();

      // 즉시 플래그 설정하고 input 초기화
      this.justSelectedFromSuggestion = true;
      this.currentMemberInput = "";

      const artist = this.allArtists.find(a => a.name?.trim() === memberName);

      if (artist) {
        if (!this.selectedMembers.some(m => m.id === artist.id)) {
          this.selectedMembers.push({
            id: artist.id,
            name: artist.name,
            type: artist.type || null,
            profileImage: artist.profileImage || null,
          });
        }
      } else {
        console.error("Artist not found in artistsWithTypes:", memberName);
      }

      // input 완전 초기화
      this.$nextTick(() => {
        this.clearMemberInput();

        // 플래그를 해제
        setTimeout(() => {
          this.justSelectedFromSuggestion = false;
        }, 300);
      });
    },

    handleMemberKeyup(event) {
      // 한글 조합 중이면 처리하지 않음
      if (this.isComposing) {
        return;
      }

      if (event.key === "Enter" || event.key === ",") {
        event.preventDefault();
        this.addMemberManually();
      }
    },

    handleMemberInput() {
      // input 이벤트로 실시간 쉼표 체크 (모바일 대응)
      if (this.isComposing) {
        return;
      }

      const memberInput = this.getNormalizedInputText(this.currentMemberInput);
      if (memberInput && memberInput.includes(",")) {
        this.$nextTick(() => {
          this.addMemberManually();
        });
      }
    },

    handleMemberBlur() {
      // 드롭다운에서 방금 선택한 경우 blur 이벤트 무시
      if (this.justSelectedFromSuggestion) {
        return;
      }

      // 한글 조합 중이면 무시
      if (this.isComposing) {
        return;
      }

      const trimmedInput = this.getNormalizedInputText(this.currentMemberInput).trim();

      // 입력값이 없으면 그냥 리턴
      if (!trimmedInput) {
        return;
      }

      // 입력값이 정확히 등록된 아티스트 이름과 일치하는지 확인
      const hasExactMatch = this.allArtists.some((artist) => artist.name === trimmedInput);

      if (hasExactMatch) {
        // 정확히 일치하는 경우만 자동 등록
        this.$nextTick(() => {
          this.addMemberManually();
        });
      } else {
        // 일치하지 않으면 input만 clear (부분 입력이므로)
        this.clearMemberInput();
      }
    },

    handleCompositionStart() {
      this.isComposing = true;
    },

    handleCompositionEnd() {
      this.isComposing = false;
      // 조합이 끝난 후 쉼표가 있으면 멤버 추가
      this.$nextTick(() => {
        if (this.getNormalizedInputText(this.currentMemberInput).includes(",")) {
          this.addMemberManually();
        }
      });
    },

    addMemberManually() {
      // 쉼표와 공백을 모두 제거하고 정리
      const memberName = this.getNormalizedInputText(this.currentMemberInput).trim().replace(/,+/g, "");

      if (memberName) {
        const artist = this.allArtists.find(a => a.name?.trim() === memberName);

        if (artist) {
          // 아티스트를 찾은 경우
          if (!this.selectedMembers.some(m => m.id === artist.id)) {
            // 아직 선택되지 않은 경우 추가
            this.selectedMembers.push({
              id: artist.id,
              name: artist.name,
              type: artist.type || null,
              profileImage: artist.profileImage || null,
            });
          }
          // 이미 선택된 경우에도 input은 clear
          this.clearMemberInput();
        } else {
          // 등록되지 않은 아티스트인 경우 경고
          console.error("Artist not found in artistsWithTypes:", memberName);
          this.$alert("등록되지 않은 아티스트입니다. 검색 목록에서 선택해주세요.");
          this.clearMemberInput();
        }
      } else if (!memberName && this.getNormalizedInputText(this.currentMemberInput).includes(",")) {
        // 쉼표만 있고 텍스트가 없는 경우도 input 초기화
        this.clearMemberInput();
      }
    },

    clearMemberInput() {
      // v-model 초기화
      this.currentMemberInput = "";

      // AutoComplete 컴포넌트 강제 초기화
      this.$nextTick(() => {
        if (this.$refs.memberAutocompleteRef) {
          const autocompleteEl = this.$refs.memberAutocompleteRef;
          // PrimeVue AutoComplete의 내부 input 접근
          const inputEl =
            autocompleteEl.$el.querySelector("input") || autocompleteEl.$el;
          if (inputEl && inputEl.tagName === "INPUT") {
            inputEl.value = "";
            inputEl.focus();
            // 커서를 처음으로 이동
            inputEl.setSelectionRange(0, 0);
          }
        }
      });
    },

    removeMember(index) {
      this.selectedMembers.splice(index, 1);
    },

    // 밴드 관련 메서드들 (개인 아티스트용)
    async searchBands(event) {
      const query = (event.query || "").toLowerCase();
      // 밴드 타입만 필터링
      const bands = this.allArtists.filter(artist => {
        // category가 'band'이거나 type에 'band'가 포함된 경우
        const isBand = artist.type &&
          (Array.isArray(artist.type) ? artist.type.includes('band') : artist.type === 'band');
        return isBand && artist.name.toLowerCase().includes(query);
      });

      // 이미 선택된 밴드는 제외
      const selectedBandIds = this.selectedBands.map(b => b.id);
      this.filteredBands = bands
        .filter(band => !selectedBandIds.includes(band.id));
      await this.loadProfileImagesForArtists(this.filteredBands.slice(0, 20));

      // 드롭다운 너비 조정
      this.$nextTick(() => {
        this.adjustDropdownWidth();
      });
    },

    addBandFromSuggestion(event) {
      const selected = event.value;
      const bandName = selected?.name?.trim();
      if (!bandName) return;

      const band = this.allArtists.find(a => {
        const isBand = a.type && (Array.isArray(a.type) ? a.type.includes('band') : a.type === 'band');
        return isBand && a.name?.trim() === bandName;
      });

      if (band && !this.selectedBands.some(b => b.id === band.id)) {
        // 플래그 설정
        this.justSelectedFromSuggestion = true;

        // 즉시 input 초기화
        this.currentBandInput = "";

        // 밴드 추가
        this.selectedBands.push({
          id: band.id,
          name: band.name,
          type: band.type || null,
          profileImage: band.profileImage || null,
        });

        // 플래그 해제 (blur 이벤트 이후)
        setTimeout(() => {
          this.justSelectedFromSuggestion = false;
        }, 300);
      }
    },

    handleBandKeyup(event) {
      // 엔터키 또는 쉼표 입력 시 멤버 추가
      if ((event.key === "Enter" || event.key === ",") && !this.isComposing) {
        event.preventDefault();
        this.addBandManually();
      }
    },

    handleBandInput(event) {
      // 쉼표 입력 감지
      if (event && event.data === ",") {
        this.$nextTick(() => {
          if (this.getNormalizedInputText(this.currentBandInput).includes(",")) {
            this.addBandManually();
          }
        });
      }
    },

    handleBandBlur() {
      // 드롭다운에서 선택한 직후면 무시
      if (this.justSelectedFromSuggestion) {
        return;
      }

      // blur 시 입력된 값이 있고, allArtists에 정확히 일치하지 않으면 초기화
      const trimmedInput = this.getNormalizedInputText(this.currentBandInput).trim();
      if (trimmedInput) {
        const bands = this.allArtists.filter(artist => {
          const isBand = artist.type &&
            (Array.isArray(artist.type) ? artist.type.includes('band') : artist.type === 'band');
          return isBand;
        });
        const exactMatch = bands.some(band => band.name === trimmedInput);

        if (!exactMatch) {
          // 정확히 일치하지 않으면 초기화 (부분 입력 방지)
          this.clearBandInput();
        }
      }
    },

    addBandManually() {
      // 쉼표와 공백을 모두 제거하고 정리
      const bandName = this.getNormalizedInputText(this.currentBandInput).trim().replace(/,+/g, "");

      if (bandName) {
        const band = this.allArtists.find(a => {
          const isBand = a.type && (Array.isArray(a.type) ? a.type.includes('band') : a.type === 'band');
          return isBand && a.name?.trim() === bandName;
        });

        if (band) {
          // 밴드를 찾은 경우
          if (!this.selectedBands.some(b => b.id === band.id)) {
            // 아직 선택되지 않은 경우 추가
            this.selectedBands.push({
              id: band.id,
              name: band.name,
              type: band.type || null,
              profileImage: band.profileImage || null,
            });
          }
          // 이미 선택된 경우에도 input은 clear
          this.clearBandInput();
        } else {
          // 등록되지 않은 밴드인 경우 경고
          this.$alert("등록되지 않은 밴드입니다. 검색 목록에서 선택해주세요.");
          this.clearBandInput();
        }
      } else if (!bandName && this.getNormalizedInputText(this.currentBandInput).includes(",")) {
        // 쉼표만 있고 텍스트가 없는 경우도 input 초기화
        this.clearBandInput();
      }
    },

    clearBandInput() {
      // v-model 초기화
      this.currentBandInput = "";

      // AutoComplete 컴포넌트 강제 초기화
      this.$nextTick(() => {
        if (this.$refs.bandAutocompleteRef) {
          const autocompleteEl = this.$refs.bandAutocompleteRef;
          // PrimeVue AutoComplete의 내부 input 접근
          const inputEl =
            autocompleteEl.$el.querySelector("input") || autocompleteEl.$el;
          if (inputEl && inputEl.tagName === "INPUT") {
            inputEl.value = "";
            inputEl.focus();
            // 커서를 처음으로 이동
            inputEl.setSelectionRange(0, 0);
          }
        }
      });
    },

    removeBand(index) {
      this.selectedBands.splice(index, 1);
    },

    // 멤버의 타입 정보 가져오기 (ID 기반)
    getMemberType(memberId) {
      const artist = this.artistsWithTypes.find(
        (artist) => artist.id === memberId
      );

      if (artist && artist.type) {
        return getArtistTypeDisplayName(artist.type);
      }

      return "미등록";
    },
    getArtistTypeLabel(type) {
      return getArtistTypeDisplayName(type);
    },
    getNormalizedInputText(value) {
      if (typeof value === "string") return value;
      if (value && typeof value === "object") {
        return value.name || value.label || "";
      }
      return "";
    },
    getArtistOptionImage(option) {
      if (option?.profileImage) return option.profileImage;
      return require("@/assets/images/default_profile.png");
    },
    getArtistImagePath(artist) {
      if (!artist) return null;
      if (artist.userProfileImg) return `profile/${artist.userProfileImg}`;
      if (artist.imgFileName) return `artist/${artist.imgFileName}`;
      return null;
    },
    async loadArtistProfileImage(artist) {
      if (!artist || artist.profileImageLoaded) return;
      if (artist.profileImage && typeof artist.profileImage === "string") {
        artist.profileImageLoaded = true;
        return;
      }
      const imagePath = this.getArtistImagePath(artist);
      if (!imagePath) {
        artist.profileImageLoaded = true;
        return;
      }
      const cacheKey = artist.id || artist.name || imagePath;
      if (this.artistImageLoadPromises[cacheKey]) {
        await this.artistImageLoadPromises[cacheKey];
        return;
      }
      const loadPromise = (async () => {
        try {
          const imageRef = storageRef(storage, imagePath);
          artist.profileImage = await getDownloadURL(imageRef);
        } catch {
          artist.profileImage = artist.profileImage || null;
        } finally {
          artist.profileImageLoaded = true;
        }
      })();
      this.artistImageLoadPromises[cacheKey] = loadPromise;
      try {
        await loadPromise;
      } finally {
        delete this.artistImageLoadPromises[cacheKey];
      }
    },
    async loadProfileImagesForArtists(artists) {
      if (!Array.isArray(artists) || artists.length === 0) return;
      const seen = new Set();
      const unique = artists.filter((a) => {
        const key = a?.id || a?.name;
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      await Promise.all(unique.map((a) => this.loadArtistProfileImage(a)));
    },

    // SNS 관련 메서드
    addSns() {
      this.snsList.push({ type: "", url: "" });
    },

    removeSns(index) {
      this.snsList.splice(index, 1);
    },

    getSnsPlaceholder(type) {
      const placeholders = {
        youtube: "https://www.youtube.com/@channelname",
        instagram: "https://www.instagram.com/username",
        twitter: "https://x.com/username",
        tiktok: "https://www.tiktok.com/@username",
        soundcloud: "https://soundcloud.com/username",
        other: "https://...",
      };
      return placeholders[type] || "URL을 입력하세요";
    },

    getSnsLabel(type) {
      const labels = {
        youtube: "유튜브",
        instagram: "인스타그램",
        twitter: "X (트위터)",
        tiktok: "틱톡",
        soundcloud: "사운드클라우드",
        other: "기타",
      };
      return labels[type] || type;
    },

    toggleSnsDropdown() {
      this.snsDropdownOpen = !this.snsDropdownOpen;
    },

    selectSnsType(type) {
      this.snsList.push({ type, url: "" });
      this.snsDropdownOpen = false;
    },

    closeSnsDropdown() {
      this.snsDropdownOpen = false;
    },

    async importFromMyProfile() {
      if (!this.userStore.isLogin) return;
      try {
        const userData = await getUserInfo(this.userStore.email);
        if (!userData) return;

        // 닉네임은 가져오기 버튼을 눌렀을 때 최신 값으로 덮어쓰기
        if (userData.nickname?.trim()) {
          this.artistName = userData.nickname.trim();
        }

        // detailLink (X 핸들 등)도 최신 값으로 덮어쓰기
        if (typeof userData.detailLink === "string") {
          this.detailLink = userData.detailLink.trim();
        }

        // 프로필 이미지는 기존 값이 있어도 artist 경로 이미지로 교체
        if (userData.img) {
          try {
            const previousUploadedImageFileName = this.uploadedImageFileName;
            if (
              previousUploadedImageFileName &&
              previousUploadedImageFileName !== this.originalImageFileName &&
              previousUploadedImageFileName !== userData.img
            ) {
              const { deleteImage } = await import("@/utils/write/storage");
              await deleteImage("artist", previousUploadedImageFileName);
            }

            try {
              await copyStorageFile(`profile/${userData.img}`, `artist/${userData.img}`);
            } catch (copyError) {
              console.warn("프로필 이미지를 artist 경로로 복사하지 못했습니다:", copyError);
            }

            const imageRef = storageRefUtil(storage, `artist/${userData.img}`);
            const url = await getDownloadURL(imageRef);
            this.profileImageUrl = url;
            this.uploadedImageFileName = userData.img;
            this.isImageDeleted = false;
          } catch (e) {
            console.warn("프로필 이미지 로드 실패:", e);
          }
        }

        this.$alert("내 정보를 가져왔어요. 필요한 부분을 추가로 수정하세요.");
      } catch (e) {
        console.error("내 정보 가져오기 오류:", e);
        this.$alert("정보를 가져오는 중 오류가 발생했습니다.");
      }
    },

    // 드롭다운 너비 조정
    adjustDropdownWidth() {
      this.$nextTick(() => {
        // 멤버 AutoComplete 드롭다운 조정
        if (this.$refs.memberAutocompleteRef) {
          const autocompleteElement = this.$refs.memberAutocompleteRef.$el;
          const panel = document.querySelector(".member-autocomplete-panel");

          if (autocompleteElement && panel) {
            const autocompleteWidth = autocompleteElement.offsetWidth;
            const autocompleteRect =
              autocompleteElement.getBoundingClientRect();

            panel.style.width = `${autocompleteWidth}px`;
            panel.style.left = `${autocompleteRect.left}px`;
            panel.style.top = `${autocompleteRect.bottom}px`;
          }
        }

        // 밴드 AutoComplete 드롭다운 조정
        if (this.$refs.bandAutocompleteRef) {
          const autocompleteElement = this.$refs.bandAutocompleteRef.$el;
          const panel = document.querySelector(".band-autocomplete-panel");

          if (autocompleteElement && panel) {
            const autocompleteWidth = autocompleteElement.offsetWidth;
            const autocompleteRect =
              autocompleteElement.getBoundingClientRect();

            panel.style.width = `${autocompleteWidth}px`;
            panel.style.left = `${autocompleteRect.left}px`;
            panel.style.top = `${autocompleteRect.bottom}px`;
          }
        }
      });
    },
  },
};
</script>

<style module>
.importBanner {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #f0f8ff;
  border: 1px solid #c8e6ff;
  border-radius: 10px;
  padding: 12px 14px;
  margin-bottom: 20px;
}

.importButton {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: #66bdff;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  font-family: "NanumSquareRoundB", sans-serif;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.2s;
  flex-shrink: 0;
}

.importButton:hover:not(:disabled) {
  background: #4a9eff;
}

.importButton:disabled {
  background: #b0d8f5;
  cursor: not-allowed;
}

.importIcon {
  width: 16px;
  height: 16px;
  filter: brightness(0) invert(1);
  display: block;
}

.importDesc {
  margin: 0;
  font-size: 12px;
  color: #6b9fc4;
  font-family: "NanumSquareRoundR", sans-serif;
  line-height: 1.4;
}

.formTypography {
  text-align: left;
  width: 100%;
  box-sizing: border-box;
}

.formTypography h5 {
  font-family: "Paperlogy5", sans-serif;
  font-style: italic;
  color: #444;
  margin: 5px;
}

.formTypography h4 {
  font-family: "Paperlogy7", sans-serif;
  font-size: 14px;
  margin: 0 0 8px 0;
  color: #444;
  font-weight: 600;
  position: relative;
}

.formTypography h4 span {
  font-size: 12px;
  font-weight: 400;
  color: #999;
  margin-left: 4px;
}

.required::after {
  content: "*";
  color: #ff4444;
  font-size: 12px;
  font-weight: bold;
  margin-left: 4px;
  vertical-align: top;
  line-height: 1;
}


.textInput {
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 10px 14px;
  font-size: 14px;
  transition: border-color 0.15s ease;
  background: #fff;
  color: #333;
  box-sizing: border-box;
}

.textInput::placeholder {
  color: #999;
}

.textInput:focus {
  outline: none;
  border-color: #66bdff;
}

.textInput:disabled {
  background: #f5f5f5;
  color: #999;
  cursor: not-allowed;
  border-color: #e0e0e0;
}

.textArea {
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  font-family: "NanumSquareRoundR", sans-serif;
  transition: border-color 0.15s ease;
  resize: vertical;
  min-height: 80px;
  box-sizing: border-box;
  background: #fff;
  color: #333;
}

.textArea:focus {
  outline: none;
  border-color: #66bdff;
}

.textArea:disabled {
  background: #f5f5f5;
  color: #999;
  cursor: not-allowed;
  border-color: #e0e0e0;
}

input[type="radio" i] {
  display: none;
}

input[type="checkbox"] {
  width: 12px;
  height: 12px !important;
  cursor: pointer;
  accent-color: #66bdff;
  margin: 0;
}

.radio {
  display: flex;
  flex-direction: row;
  gap: 8px;
  margin-top: 4px;
  flex-wrap: wrap;
}

.radio label {
  font-size: 14px;
  font-family: "NanumSquareRoundR", sans-serif;
  padding: 10px 20px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: #fff;
  color: #666;
  cursor: pointer;
  text-align: center;
  transition: all 0.15s ease;
  font-weight: 400;
  position: relative;
  display: block;
  flex: 1;
  min-width: 80px;
}

.radio label:hover {
  border-color: #66bdff;
  color: #66bdff;
}

.radio label:has(input[type="radio"]:checked) {
  border-color: #66bdff;
  background: #66bdff;
  color: #fff;
  font-weight: 500;
}

.checkbox {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 4px;
}

.checkbox label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-family: "NanumSquareRoundR", sans-serif;
  cursor: pointer;
  color: #333;
}

.checkbox input[type="checkbox"] {
  margin: 0;
  cursor: pointer;
}

.checkbox input[type="checkbox"]:disabled {
  cursor: not-allowed;
}

/* 선택된 포지션 우선순위 스타일 */
.selectedPositions {
  margin-top: 12px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.formTypography .selectedPositions h5 {
  margin: 0 0 8px 0;
  font-size: 13px;
  font-weight: bold;
  color: #495057;
  font-family: "NanumSquareRoundB", sans-serif;
}

.positionList {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.positionItem {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 10px;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  transition: all 0.2s;
}

.positionItem:hover {
  border-color: #66bdff;
  box-shadow: 0 2px 4px rgba(102, 189, 255, 0.1);
}

.positionRank {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  background: #66bdff;
  color: white;
  border-radius: 50%;
  font-size: 11px;
  font-weight: bold;
  font-family: "NanumSquareRoundB", sans-serif;
}

.positionName {
  flex: 1;
  font-size: 13px;
  font-family: "NanumSquareRoundR", sans-serif;
  color: #495057;
}

.positionControls {
  display: flex;
  gap: 4px;
}

.moveButton,
.removeButton {
  width: 26px;
  height: 26px;
  border: none;
  border-radius: 4px;
  font-size: 11px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.moveButton {
  background: #6c757d;
  color: white;
}

.moveButton:hover:not(:disabled) {
  background: #5a6268;
}

.moveButton:disabled {
  background: #e9ecef;
  color: #adb5bd;
  cursor: not-allowed;
}

.removeButton {
  background: #dc3545;
  color: white;
}

.removeButton:hover:not(:disabled) {
  background: #c82333;
}

.removeButton:disabled {
  background: #e9ecef;
  color: #adb5bd;
  cursor: not-allowed;
}

/* 멤버 관련 스타일 */
.selectedMembers {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 10px;
  min-height: 20px;
  padding: 5px;
  border-radius: 4px;
}

.memberTag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #66bdff;
  color: #fff;
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 13px;
  font-family: "NanumSquareRoundR", sans-serif;
  border: none;
  max-width: 200px;
  transition: background-color 0.15s ease;
}

.memberTag:hover {
  background: #4a9eff;
}

.memberType {
  opacity: 0.8;
  font-size: 11px;
  font-weight: normal;
}

.removeButton {
  background: rgba(255, 255, 255, 0.3);
  border: none;
  color: #fff;
  font-size: 16px;
  font-weight: bold;
  margin-left: 6px;
  cursor: pointer;
  padding: 0;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.15s ease;
  line-height: 1;
}

.removeButton:hover {
  background-color: rgba(255, 255, 255, 0.5);
}

.removeButton:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.autocomplete {
  width: 100%;
  font-size: 14px;
  position: relative;
}

.memberAutocomplete {
  font-family: "NanumSquareRoundR", sans-serif;
}

.artistOption {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
}

.optionProfileImage {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}

.optionDetails {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.optionName {
  font-size: 13px;
  color: #333;
  font-family: "NanumSquareRoundB", sans-serif;
}

.optionType {
  font-size: 11px;
  color: #666;
  font-family: "NanumSquareRoundR", sans-serif;
}

.artistList {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 12px;
}

.artistItem {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.artistInfo {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
}

.selectedProfileWrap {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  overflow: hidden;
  background: #f2f5f9;
  border: 1px solid #e6e9ef;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.selectedProfileImage {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.artistDetails {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.artistName {
  font-size: 14px;
  color: #333;
  font-family: "NanumSquareRoundB", sans-serif;
  word-break: break-word;
}

.artistType {
  font-size: 12px;
  color: #666;
  font-family: "NanumSquareRoundR", sans-serif;
}

.memberRemoveButton {
  padding: 6px 12px;
  background: #f44336;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  font-family: "NanumSquareRoundR", sans-serif;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;
  margin-left: 12px;
}

.memberRemoveButton:hover:not(:disabled) {
  background: #d32f2f;
}

.memberRemoveButton:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.addArtistForm {
  display: flex;
  gap: 8px;
  align-items: center;
}

.addArtistForm .memberAutocomplete {
  flex: 1;
}

.addButton {
  height: 40px;
  min-height: 40px;
  padding: 0 16px;
  background: #2196f3;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  font-family: "NanumSquareRoundR", sans-serif;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.addButton:hover:not(:disabled) {
  background: #1976d2;
}

.addButton:disabled {
  background: #ccc;
  cursor: not-allowed;
}

:global(.p-autocomplete) {
  width: 100%;
  display: flex;
  position: relative;
}

:global(.p-autocomplete-input) {
  border: 1px solid #ddd;
  border-right: none;
  border-radius: 6px 0px 0px 6px;
  padding: 10px 14px;
  height: 40px;
  font-size: 14px;
  width: 100%;
  box-sizing: border-box;
  font-family: "NanumSquareRoundR", sans-serif;
  flex: 1;
  transition: border-color 0.15s ease;
  background: #fff;
  color: #333;
}

:global(.p-autocomplete-input::placeholder) {
  color: #999;
}

:global(.p-autocomplete-input:focus) {
  border-color: #66bdff;
  border-right: none;
  outline: none;
  box-shadow: none;
}

:global(.p-autocomplete-input:focus)+ :global(.p-autocomplete-dropdown) {
  border-color: #66bdff;
  border-left: none;
}

:global(.p-autocomplete-dropdown) {
  border: 1px solid #ddd;
  border-left: none;
  border-radius: 0 6px 6px 0;
  height: 40px;
  min-height: 40px;
  background: #fff;
  color: #666;
  transition: border-color 0.15s ease;
  flex-shrink: 0;
  width: 40px;
  padding: 0;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
}

:global(.p-autocomplete-dropdown:hover) {
  background: #fafafa;
  border-color: #66bdff;
}

:global(.p-autocomplete-panel) {
  border: 1px solid #ddd;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  background: #fff;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  width: auto;
  min-width: 100%;
  margin-top: 2px;
}

:global(.member-autocomplete-panel) {
  z-index: 1200 !important;
}

:global(.p-autocomplete-item) {
  padding: 10px 14px;
  font-size: 14px;
  cursor: pointer;
  font-family: "NanumSquareRoundR", sans-serif;
  color: #333;
  transition: background-color 0.15s ease;
  border-bottom: 1px solid #f5f5f5;
}

:global(.p-autocomplete-item:last-child) {
  border-bottom: none;
}

:global(.p-autocomplete-item:hover) {
  background-color: #f8f9fa;
}

:global(.p-autocomplete-item-highlight) {
  background-color: #f0f7ff;
  color: #66bdff;
}

:global(.p-autocomplete-dropdown-icon) {
  color: #666;
  font-size: 12px;
  display: none;
}

:global(.p-autocomplete-loading-icon) {
  display: none;
}

:global(.p-autocomplete-empty-message) {
  padding: 0px 10px;
  font-size: 14px;
}

:global(.p-icon) {
  display: none;
}

.input {
  padding: 0;
  margin-bottom: 20px;
}

.buttons {
  margin-top: 24px;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  padding-top: 20px;
  border-top: 1px solid #eee;
}

.cancel {
  padding: 12px 24px;
  border-radius: 6px;
  background: #f5f5f5;
  color: #666;
  border: 1px solid #ddd;
  min-width: 80px;
  font-size: 14px;
  font-weight: 500;
  font-family: "NanumSquareRoundR", sans-serif;
  cursor: pointer;
  transition: all 0.15s ease;
}

.cancel:hover:not(:disabled) {
  background: #e8e8e8;
  border-color: #ccc;
}

.cancel:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.register {
  padding: 12px 24px;
  border-radius: 6px;
  background: #66bdff;
  color: #fff;
  border: none;
  min-width: 80px;
  font-size: 14px;
  font-weight: 500;
  font-family: "NanumSquareRoundR", sans-serif;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.register:hover:not(:disabled) {
  background: #4a9eff;
}

.register:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.spinner {
  display: inline-block;
  width: 15px;
  height: 15px;
  border: 2px solid transparent;
  border-top: 2px solid #fff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-right: 8px;
  box-sizing: border-box;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

/* SNS 섹션 스타일 */
.snsSection {
  margin-bottom: 22px;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 10px;
  border: 1px solid #e9ecef;
}

.snsSectionTitle {
  margin-bottom: 12px;
  font-size: 15px;
  font-weight: bold;
  color: #333;
  font-family: "NanumSquareRoundB", sans-serif;
}

.snsRow {
  display: flex;
  gap: 8px;
  margin-bottom: 10px;
  align-items: center;
}

.snsTypeLabel {
  flex-shrink: 0;
  min-width: 90px;
  padding: 10px 12px;
  background: linear-gradient(135deg, #66bdff, #ff6ba9);
  color: white;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  font-family: "NanumSquareRoundB", sans-serif;
  text-align: center;
}

.snsDropdownWrapper {
  position: relative;
  width: 100%;
}

.snsDropdownToggle {
  width: 100%;
  padding: 10px 14px;
  border: 2px dashed #dee2e6;
  border-radius: 8px;
  font-size: 13px;
  font-family: "NanumSquareRoundR", sans-serif;
  background: white;
  color: #6c757d;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.snsDropdownToggle:hover:not(:disabled) {
  border-color: #66bdff;
  color: #66bdff;
}

.snsDropdownToggle:disabled {
  background: #f8f9fa;
  color: #adb5bd;
  cursor: not-allowed;
}

.dropdownArrow {
  font-size: 10px;
  transition: transform 0.2s ease;
}

.dropdownArrowOpen {
  transform: rotate(180deg);
}

.snsDropdownMenu {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 100;
  overflow: hidden;
}

.snsDropdownItem {
  width: 100%;
  padding: 10px 14px;
  border: none;
  background: white;
  text-align: left;
  font-size: 13px;
  font-family: "NanumSquareRoundR", sans-serif;
  color: #333;
  cursor: pointer;
  transition: background 0.2s ease;
}

.snsDropdownItem:hover {
  background: #f0f7ff;
  color: #66bdff;
}

.snsDropdownItem:not(:last-child) {
  border-bottom: 1px solid #f0f0f0;
}

.snsInput {
  flex: 1;
  padding: 10px 14px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 13px;
  font-family: "NanumSquareRoundR", sans-serif;
  transition: all 0.3s ease;
  background: white;
}

.snsInput:focus {
  outline: none;
  border-color: #66bdff;
  box-shadow: 0 0 0 3px rgba(102, 189, 255, 0.1);
}

.snsInput:disabled {
  background: #f8f9fa;
  color: #6c757d;
  cursor: not-allowed;
}

.snsInput::placeholder {
  color: #adb5bd;
  font-style: italic;
}

.snsRemoveBtn {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 6px;
  background: #ff6b6b;
  color: white;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.snsRemoveBtn:hover:not(:disabled) {
  background: #ee5a5a;
  transform: scale(1.05);
}

.snsRemoveBtn:disabled {
  background: #ccc;
  cursor: not-allowed;
}


/* ========================================
   Small (~480px)
   ======================================== */
@media (max-width: 480px) {
  .formTypography h4 {
    font-size: 13px;
    margin: 0 0 6px 0;
  }

  .formTypography h4 span {
    font-size: 11px !important;
  }

  input {
    font-size: 14px;
    height: 38px !important;
  }

  .textInput {
    padding: 8px 12px;
  }

  .input {
    margin-bottom: 18px;
  }

  .textArea {
    padding: 8px 12px;
    min-height: 70px;
  }

  .radio {
    gap: 6px;
  }

  .radio label {
    font-size: 13px;
    padding: 8px 16px;
    min-width: 70px;
  }

  .checkbox {
    gap: 6px;
  }

  .checkbox label {
    font-size: 13px;
    gap: 6px;
  }

  .selectedPositions {
    padding: 10px;
    margin-top: 10px;
    border-radius: 6px;
  }

  .formTypography .selectedPositions h5 {
    font-size: 12px;
    margin-bottom: 6px;
  }

  .positionList {
    gap: 5px;
  }

  .positionItem {
    padding: 6px 8px;
    gap: 6px;
    border-radius: 5px;
  }

  .positionRank {
    width: 20px;
    height: 20px;
    font-size: 10px;
  }

  .positionName {
    font-size: 12px;
  }

  .positionControls {
    gap: 3px;
  }

  .moveButton,
  .removeButton {
    width: 22px;
    height: 22px;
    font-size: 10px;
    border-radius: 3px;
  }

  .selectedMembers {
    padding: 6px;
    gap: 5px;
    min-height: 36px;
    border-radius: 5px;
  }

  .memberTag {
    font-size: 11px;
    padding: 4px 8px;
    border-radius: 12px;
    gap: 4px;
  }

  .memberType {
    font-size: 10px;
  }

  .removeButton {
    width: 16px;
    height: 16px;
    font-size: 10px;
  }

  .buttons {
    margin-top: 20px;
    flex-direction: column-reverse;
    gap: 8px;
  }

  .cancel,
  .register {
    width: 100%;
    padding: 12px;
    font-size: 14px;
  }

  .spinner {
    width: 14px;
    height: 14px;
    margin-right: 6px;
  }

  :global(.p-autocomplete-input) {
    padding: 8px 12px;
    height: 38px;
    border-radius: 6px 0 0 6px;
  }

  :global(.p-autocomplete-dropdown) {
    height: 38px;
    width: 38px;
    border-radius: 0 6px 6px 0;
  }

  .addButton {
    height: 38px;
    min-height: 38px;
    padding: 0 14px;
  }

  :global(.p-autocomplete-panel) {
    max-height: 180px;
  }

  :global(.p-autocomplete-item) {
    padding: 8px 12px;
    font-size: 13px;
  }

  .selectedMembers {
    gap: 5px;
    padding: 8px;
  }

  .memberTag {
    padding: 5px 10px;
    font-size: 12px;
    max-width: 160px;
  }

  .removeButton {
    width: 16px;
    height: 16px;
    font-size: 14px;
    margin-left: 5px;
  }

  .snsSection {
    padding: 14px;
    border-radius: 8px;
  }

  .snsSectionTitle {
    font-size: 14px;
    margin-bottom: 10px;
  }

  .snsRow {
    gap: 6px;
    margin-bottom: 8px;
  }

  .snsTypeLabel {
    min-width: 75px;
    padding: 8px 10px;
    font-size: 11px;
    border-radius: 6px;
  }

  .snsInput {
    padding: 8px 10px;
    font-size: 12px;
    border-radius: 6px;
  }

  .snsInput::placeholder {
    font-size: 11px;
  }

  .snsRemoveBtn {
    width: 28px;
    height: 28px;
    font-size: 12px;
    border-radius: 5px;
  }

  .snsDropdownToggle {
    padding: 8px 10px;
    font-size: 12px;
    border-radius: 6px;
  }

  .snsDropdownMenu {
    border-radius: 6px;
  }

  .snsDropdownItem {
    padding: 8px 10px;
    font-size: 12px;
  }

  .artistList {
    gap: 6px;
    margin-bottom: 10px;
  }

  .artistItem {
    padding: 8px 10px;
    border-radius: 6px;
  }

  .artistName {
    font-size: 13px;
  }

  .artistType {
    font-size: 11px;
  }

  .addArtistForm {
    flex-direction: column;
    gap: 6px;
  }

  .addButton {
    width: 100%;
    padding: 10px 14px;
  }

  .memberRemoveButton {
    padding: 5px 10px;
    font-size: 11px;
    margin-left: 8px;
  }
}
</style>
